import { Field } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import Details from "Catalog/Edit/shared/components/Details"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import StatefulField from "shared/components/StatefulField"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import useStatefulField from "shared/hooks/useStatefulField"
import { IOptionList } from "shared/hubrise/models"
import * as OptionList from "shared/hubrise/utils/OptionList"

import Commands from "./Commands"
import Options from "./Options"
import { RepeatableCheckbox } from "./Styles"
import Usage from "./Usage"

const NUMBER_REGEXP = /^\d*$/

type Input = {
  minSelections: string
  maxSelections: string
  multipleSelection: boolean
  nbOptions: number // Update this value to force revalidation when options are added or deleted
}

type Error = {
  min_selections?: string
  max_selections?: string
}

const Content = ({ optionList }: { optionList: IOptionList }): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigateNoUpdate()

  const { editor } = useEditor()
  const { catalogNavigation } = useCatalogNavigation()

  const mergeEditorValue = ({
    minSelections,
    maxSelections,
    multipleSelection,
  }: Pick<Input, "minSelections" | "maxSelections" | "multipleSelection">): IOptionList => ({
    ...optionList,
    min_selections: Number(minSelections),
    max_selections: maxSelections === "" ? null : Number(maxSelections),
    multiple_selection: multipleSelection,
  })

  const { value, error, handleChange } = useStatefulField<Input, Error>({
    initialValue: {
      minSelections: String(optionList.min_selections),
      maxSelections: optionList.max_selections === null ? "" : String(optionList.max_selections),
      multipleSelection: optionList.multiple_selection,
      nbOptions: optionList.options.length,
    },
    validate: (newValue) => {
      const error: Error = {}

      if (newValue.minSelections.length === 0) {
        error.min_selections = t("validations.not_empty")
      } else if (newValue.maxSelections.length > 0 && Number(newValue.minSelections) > Number(newValue.maxSelections)) {
        error.min_selections = t("option_list.min_selections.validation.higher_than_max")
      } else if (Number(newValue.minSelections) > optionList.options.length) {
        error.min_selections = t("option_list.min_selections.validation.too_high", { count: optionList.options.length })
      }

      if (newValue.maxSelections.length > 0 && Number(newValue.maxSelections) === 0) {
        error.max_selections = t("option_list.max_selections.validation.not_zero")
      } else if (
        newValue.maxSelections.length > 0 &&
        Number(newValue.maxSelections) > optionList.options.length &&
        !newValue.multipleSelection
      ) {
        error.max_selections = t("option_list.max_selections.validation.too_high", { count: optionList.options.length })
      }

      return Object.values(error).some((e) => e !== undefined) ? error : undefined
    },
    onChange: (newValue, dangerouslyUpdateValue) => {
      const editorValue = mergeEditorValue(newValue)

      // Disable multiple selection if not applicable
      if (!OptionList.canRepeatOptions(editorValue)) {
        if (editorValue.multiple_selection) dangerouslyUpdateValue({ ...newValue, multipleSelection: false })
      }

      // Update changed values in the editor
      if (optionList.min_selections !== editorValue.min_selections) {
        editor.optionList.update(optionList.id, "min_selections", editorValue.min_selections)
      }

      if (optionList.max_selections !== editorValue.max_selections) {
        editor.optionList.update(optionList.id, "max_selections", editorValue.max_selections)
      }

      if (optionList.multiple_selection !== editorValue.multiple_selection) {
        editor.optionList.update(optionList.id, "multiple_selection", editorValue.multiple_selection)
      }
    },
  })

  React.useEffect(() => {
    // Trigger re-validation when options are added or deleted
    if (value.nbOptions !== optionList.options.length) {
      handleChange({ ...value, nbOptions: optionList.options.length })
    }
  }, [handleChange, optionList.options.length, value])

  return (
    <>
      <Details.Topbar
        commands={<Commands optionList={optionList} />}
        onClose={() => navigate(catalogNavigation.editPath("option_lists"))}
      >
        <StatefulField.Title
          name="option-list-name"
          initialValue={optionList.name}
          placeholder={t("option_list.name.placeholder")}
          onChange={(name) => editor.optionList.update(optionList.id, "name", name)}
          validate={(name) => (name.length === 0 ? t("validations.not_empty") : undefined)}
        />
      </Details.Topbar>

      <Details.SplitHalf>
        <Field.Input
          name="option-list-min-selections"
          value={String(value.minSelections)}
          label={t("option_list.min_selections.label")}
          error={error?.min_selections}
          onChange={(minSelections) => handleChange({ ...value, minSelections })}
          filter={NUMBER_REGEXP}
        />

        <Field.Input
          name="option-list-max-selections"
          value={value.maxSelections}
          label={t("option_list.max_selections.label")}
          error={error?.max_selections}
          placeholder={t("option_list.max_selections.placeholder")}
          onChange={(maxSelections) => handleChange({ ...value, maxSelections })}
          filter={NUMBER_REGEXP}
        />
      </Details.SplitHalf>

      <RepeatableCheckbox
        name="option-list-multiple-selection"
        $visible={OptionList.canRepeatOptions(mergeEditorValue(value))}
        value={value.multipleSelection}
        text={t("option_list.multiple_selection.label")}
        onChange={(multipleSelection) => handleChange({ ...value, multipleSelection })}
      />

      <Options optionList={optionList} />

      <Usage optionList={optionList} />
    </>
  )
}

export default React.memo(Content)
